import React, {Suspense, useEffect, useRef, useState} from "react";
import * as Sentry from "@sentry/browser";
import {isSearchPage, useProductListContext} from "../productListProvider/ProductListProvider";
import {Trans, useTranslation} from "react-i18next";
import ListItemCard, {scrollInfoStorage} from "./ListItemCard";
import {getShopId} from "../../lib/getShopId";
import PatternInterruption from "./PatternInterruption";
import {Tracking} from "../../../tracking/tracking";
import {IItemContent, ProductListFilterItemValueType} from "../../../typescript-library";
import isSameObject from "../../../helpers/isSameObject";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import ErrorBoundary, {HandleErrorType} from "../../utils/ErrorBoundary";
import {useCustomerSelector} from "../../../store";

/**
 * The component showing up the product tiles. If no items are
 * present and still loading, a loading spinner is shown.<br/>
 * If not loading there will be a note saying 'No articles present'
 * @constructor
 */
const ListItemsComponent: React.FC = () => {
  const {state} = useProductListContext();
  const {t} = useTranslation();

  const ProductListLoader: React.FC = () => {
    const [productListLoading, setProductListLoading] = useState(false);

    useEffect(() => {
      if (state.loading) {
        setProductListLoading(true);
      } else {
        setProductListLoading(false);
      }
    }, []);

    if (productListLoading) {
      return (
        <div className="product-list__loader py-11">
          <div className="product-list__loader--sticky">
            <LoadingSpinner />
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  if (!state.loading && !state.items.length) {
    if (isSearchPage) {
      const query = new URLSearchParams(window.location.search).get("q");
      if (query) {
        return (
          <div className="suspenses-container">
            <Trans
              i18nKey="search.noResultsGiven--query"
              ns="translations"
              components={{
                s: <strong />,
              }}
              values={{query}}
            />
          </div>
        );
      } else {
        return (
          <React.Fragment>
            <div key={0} className="suspenses-container">
              <div className="h3">{t("search.tryAgain")}</div>
            </div>
            <div key={1} className="suspenses-container">
              <p>{t("search.pleaseClickMagnifyingGlass")}</p>
            </div>
          </React.Fragment>
        );
      }
    } else {
      return <div className="suspenses-container">{t("product.noMatchingItems")}</div>;
    }
  }

  return (
    <div className="product-list grid pb-3">
      <ProductListLoader />
      <ItemCards />
    </div>
  );
};

const ListItems = () => {
  const handleError: HandleErrorType = (error, occurrence, additional) => {
    Sentry.captureException(error, {
      extra: {additional},
      tags: {occurrence},
    });
  };

  return (
    <ErrorBoundary handleError={handleError} fallback={null} occurrence="ListItems">
      <Suspense fallback={null}>
        <ListItemsComponent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default ListItems;

const currentHeadline = document.body.querySelector<HTMLElement>(".product-list-react")?.dataset.headline;

const ItemCards: React.FC = () => {
  const {state} = useProductListContext();
  const oldListRef = useRef<number[]>([]);
  const oldFiltersRef = useRef<{[p: string]: ProductListFilterItemValueType}>({});
  const {customer} = useCustomerSelector();

  const scrollInfo = scrollInfoStorage.read(document.body.id);

  const isItemClicked = useRef(false);

  useEffect(() => {
    const saveScrollPosition = () => {
      if (!isItemClicked.current) {
        scrollInfoStorage.write(document.body.id, {itemId: null, position: window.scrollY});
      }
    };
    window.addEventListener("beforeunload", saveScrollPosition);

    if (scrollInfo && scrollInfo.itemId === null && isItemClicked.current) {
      const {position} = scrollInfo;
      if (typeof window.scrollTo === "function") {
        window.scrollTo({left: 0, top: position});
        isItemClicked.current = false;
      } else {
        window.scroll(0, position);
      }
      scrollInfoStorage.remove(document.body.id);
    }
  }, []);

  useEffect(() => {
    const isSameFilterSet = isSameObject<{[p: string]: ProductListFilterItemValueType}>(
      state.filters,
      oldFiltersRef.current,
    );
    const diffItems: {index: number; item: IItemContent}[] = [];
    state.items.forEach((itm, index) => {
      // if state.items has new item on particular index, or it's the same by coincidence from a new filter setting
      if (oldListRef.current[index] !== itm.itemId || !isSameFilterSet) {
        diffItems.push({item: itm, index});
      }
    });

    const itemsWithoutPrices = diffItems.filter((item) => !item.item.prices || !item.item.prices.length);
    if (itemsWithoutPrices.length) {
      const e = new Error("No prices for certain items found");
      e.name = "NoPricesError";
      const itemIds = itemsWithoutPrices.map((iwp) => iwp.item.itemId);
      if (__DEVELOPMENT__) {
        /* eslint-disable no-console */
        console.log("DEVELOPMENT:", e);
        console.log("cGroupId:", customer.customerGroupId, ", items without prices:", itemIds);
        /* eslint-enable no-console */
      }

      // Send a cumulated error to Sentry
      Sentry.captureException(e, {
        extra: {itemIds, customerGroupId: customer.customerGroupId},
        tags: {occurrence: "listitems"},
      });
    }

    Tracking.trackProductImpressions(diffItems, currentHeadline);

    oldListRef.current = state.items.map((itm) => itm.itemId);
    oldFiltersRef.current = state.filters;
  }, [state.items]);

  const elementsArray = state.items
    .filter((item) => {
      return !!item.prices && !!item.prices.length;
    })
    .map((item, index) => {
      const onClick = () => {
        scrollInfoStorage.write(document.body.id, {itemId: item.itemId, position: window.scrollY});
        isItemClicked.current = true;

        Tracking.trackImpressionClicked(
          item,
          index,
          document.body.querySelector<HTMLElement>(".product-list-react")?.dataset.headline,
        );
      };

      // Color the item tiles, if duplicate in the list (develop)
      const length = __PRODUCTION__ ? 1 : state.items.filter((itm) => itm.itemId === item.itemId).length;

      return (
        <ListItemCard
          length={length}
          key={index}
          item={item}
          onClick={onClick}
          swipeable={false}
          scrollInfo={scrollInfo}
          lazyLoading={index >= 3}
          customClass={`column-4 column-m-2 ${state.showFilters ? "column-l-4" : "column-l-3"}`}
        />
      );
    });
  if (getShopId() === 100001 && state.items.length >= 12 && document.body.id !== "C100799") {
    elementsArray.splice(
      12,
      0,
      <PatternInterruption key="pattern-interruption" customClass={"column-8 column-l-12"} />,
    );
  }

  return <>{elementsArray}</>;
};
